import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/contexts/AuthContext'
import { accountApiService, accountQueries } from '@/services/accountApi'
import type { Contact } from '@/types/account'

/**
 * Fetches all contacts (up to 500) and returns a Map keyed by normalised
 * phone-number string so callers can look up whether a given number already
 * belongs to a saved contact.
 *
 * Normalisation strips all non-digit characters except a leading '+'.
 */
function normalise(number: string): string {
  // Keep a leading '+', strip everything else that isn't a digit
  const hasPlus = number.trimStart().startsWith('+')
  const digits = number.replace(/\D/g, '')
  return hasPlus ? `+${digits}` : digits
}

export function useContactLookup() {
  const { isAuthenticated } = useAuth()

  const { data: contacts = [] } = useQuery({
    queryKey: accountQueries.contactsLookupBase,
    queryFn: async (): Promise<Contact[]> => {
      // Fetch a large page — for lookup purposes we want the full list
      const response = await accountApiService.getContacts(1, 500)
      return response.contacts
    },
    enabled: isAuthenticated,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  })

  // Build a map of normalised number → contact for O(1) lookups
  const numberMap = new Map<string, Contact>()
  for (const contact of contacts) {
    for (const pn of contact.phoneNumbers ?? []) {
      numberMap.set(normalise(pn.number), contact)
    }
  }

  /**
   * Returns the Contact whose phone numbers include this number, or undefined.
   */
  function lookup(number: string): Contact | undefined {
    return numberMap.get(normalise(number))
  }

  return { lookup, contacts }
}
